import { dayjs } from '@flash-tecnologia/hros-web-ui-v2';
import { IndividualShareHolder } from 'bff/src/domain/model/compliance.model';
import { useState } from 'react';
import { isValidCPF } from 'utils/cpfValidation';

export const useIndividualShareholder = (): [
  typeof individualShareholder,
  typeof setindividualShareholder,
  typeof validate,
  typeof errors,
] => {
  const [name, setName] = useState<IndividualShareHolder['name']>('');
  const [nameError, setNameError] = useState<string>();

  const [document, setDocument] =
    useState<IndividualShareHolder['document']>('');
  const [documentError, setDocumentError] = useState<string>();

  const [participation, setParticipation] =
    useState<IndividualShareHolder['participation']>(0);
  const [participationError, setParticipationError] = useState<string>();

  const [birthDate, setBirthdate] =
    useState<IndividualShareHolder['birthDate']>('');
  const [birthDateError, setBirthdateError] = useState<string>();

  const [position, setPosition] =
    useState<IndividualShareHolder['position']>('');
  const [positionError, setPositionError] = useState<string>();

  const [address, setAddress] = useState<IndividualShareHolder['address']>('');
  const [addressError, setAddressError] = useState<string>();

  const [isPEP, setIsPEP] = useState<IndividualShareHolder['isPEP']>(false);

  const resetState = () => {
    setName('');
    setNameError(undefined);
    setDocument('');
    setDocumentError(undefined);
    setParticipation(0);
    setParticipationError(undefined);
    setBirthdate('');
    setBirthdateError(undefined);
    setPosition('');
    setPositionError(undefined);
    setAddress('');
    setAddressError(undefined);
    setIsPEP(false);
  };

  const validateName = () => {
    if (!name) {
      setNameError('Campo é obrigatório!');
      return false;
    }
    if (name.split(' ').length < 2) {
      setNameError('Campo deve conter seu nome completo!');
      return false;
    }
    setNameError('');
    return true;
  };

  const validateDocument = () => {
    if (!document) {
      setDocumentError('Campo é obrigatório!');
      return false;
    }
    if (!isValidCPF(document)) {
      setDocumentError('CPF não é válido!');
      return false;
    }
    setDocumentError('');
    return true;
  };

  const validateParticipation = () => {
    if (!participation) {
      setParticipationError('Campo é obrigatório!');
      return false;
    }
    if (participation <= 0 || participation > 100) {
      setParticipationError(
        'Utilize um número maior que zero e menor ou igual a 100',
      );
      return false;
    }
    setParticipationError('');
    return true;
  };

  const validateBirthDate = () => {
    if (!birthDate) {
      setBirthdateError('Campo é obrigatório!');
      return false;
    }

    if (dayjs().diff(dayjs(birthDate), 'days') <= 0) {
      setBirthdateError('Selecione uma data anterior ao dia de hoje');
      return false;
    }

    setBirthdateError('');
    return true;
  };

  const validatePosition = () => {
    if (!position) {
      setPositionError('Campo é obrigatório!');
      return false;
    }
    if (position.length < 2) {
      setPositionError('Campo deve conter um cargo válido!');
      return false;
    }
    setPositionError('');
    return true;
  };

  const validateAddress = () => {
    if (!address) {
      setAddressError('Campo é obrigatório!');
      return false;
    }
    if (address.split(' ').length < 2) {
      setAddressError('Campo deve conter um endereço válido');
      return false;
    }
    setAddressError('');
    return true;
  };

  const validate = (): boolean => {
    const isNameValid = validateName();
    const isDocumentValid = validateDocument();
    const isParticipationValid = validateParticipation();
    const isBirthDateValid = validateBirthDate();
    const isPositionValid = validatePosition();
    const isAddressValid = validateAddress();
    return (
      isNameValid &&
      isDocumentValid &&
      isParticipationValid &&
      isBirthDateValid &&
      isPositionValid &&
      isAddressValid
    );
  };

  const individualShareholder = {
    name,
    document,
    participation,
    birthDate,
    position,
    address,
    isPEP,
  };

  const setindividualShareholder = {
    setName,
    setNameError,
    setDocument,
    setDocumentError,
    setParticipation,
    setParticipationError,
    setBirthdate,
    setBirthdateError,
    setPosition,
    setPositionError,
    setAddress,
    setAddressError,
    setIsPEP,
    resetState,
    setAll: (data: Partial<IndividualShareHolder>) => {
      if (data.name !== undefined) setName(data.name);
      if (data.document !== undefined) setDocument(data.document);
      if (data.participation !== undefined)
        setParticipation(data.participation);
      if (data.birthDate !== undefined) setBirthdate(data.birthDate);
      if (data.position !== undefined) setPosition(data.position);
      if (data.address !== undefined) setAddress(data.address);
      if (data.isPEP !== undefined) setIsPEP(data.isPEP);
    },
  };

  const errors = {
    nameError,
    documentError,
    participationError,
    birthDateError,
    positionError,
    addressError,
  };

  return [individualShareholder, setindividualShareholder, validate, errors];
};
